import { useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { ArrowRight, Calendar, Clock, ChevronRight } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { usePageMeta } from "../hooks/usePageMeta";

// 12 months of blog posts
const blogPosts = [
  {
    id: "why-your-estimate-keeps-blowing-out",
    title: "Why Your Estimate Keeps Blowing Out (And How to Fix It)",
    excerpt: "If you've ever finished a job wondering where your margin went, you're not alone. Here's the real reason most builder estimates fail, and it's not what you think.",
    date: "2026-02-01",
    readTime: "5 min read",
    category: "Estimating",
    image: "https://customer-assets.emergentagent.com/job_builder-estimates/artifacts/1f491teq_IMG_1076.jpeg",
    featured: true,
  },
  {
    id: "materials-takeoff-vs-full-estimate",
    title: "Materials Takeoff vs Full Estimate: Which Do You Actually Need?",
    excerpt: "One gives you quantities. The other gives you a price. Here's how to know which output is right for your next job.",
    date: "2026-01-15",
    readTime: "4 min read",
    category: "Services",
    image: "https://customer-assets.emergentagent.com/job_builder-estimates/artifacts/rocwoews_rickydee_69839_Modern_Single_level_home_minimalist_design_flo_62bc35dc-0af4-4bab-9296-e61166c9f014_3.jpeg",
  },
  {
    id: "hidden-costs-residential-builds",
    title: "The Hidden Costs That Kill Residential Builds",
    excerpt: "Temporary works, site access, services connections, the stuff that's never on the drawings but always ends up on your invoice.",
    date: "2026-01-01",
    readTime: "6 min read",
    category: "Residential",
    image: "https://customer-assets.emergentagent.com/job_builder-estimates/artifacts/4fol0kva_IMG_1579.JPG",
  },
  {
    id: "pricing-civil-work-drainage-earthworks",
    title: "How to Price Civil Work Without Getting Burnt",
    excerpt: "Drainage, earthworks, and retaining walls have a way of going sideways fast. Here's what to watch for.",
    date: "2025-12-15",
    readTime: "5 min read",
    category: "Civil",
    image: "https://customer-assets.emergentagent.com/job_builder-estimates/artifacts/8hafomek_IMG_3491%20%281%29.jpeg",
  },
  {
    id: "labour-allowances-that-hold-up",
    title: "Labour Allowances That Actually Hold Up On Site",
    excerpt: "Fantasy hours from a spreadsheet don't pay your crew. Here's how to build labour allowances that survive contact with reality.",
    date: "2025-12-01",
    readTime: "5 min read",
    category: "Labour",
    image: "https://customer-assets.emergentagent.com/job_builder-estimates/artifacts/s0jpeqfb_IMG_2977.jpeg",
  },
  {
    id: "scope-boundaries-protect-margin",
    title: "How Clear Scope Boundaries Protect Your Margin",
    excerpt: "The client thinks it was included. You thought it was excluded. Here's how to avoid that conversation.",
    date: "2025-11-15",
    readTime: "4 min read",
    category: "Estimating",
    image: "https://customer-assets.emergentagent.com/job_builder-estimates/artifacts/wfjc1s9h_volavechop_fireplace_covered_with_steel_sheets_in_a_modern_mini_18bfb596-61b7-4eb4-9b22-7a38c8bb56b0.png",
  },
  {
    id: "winning-work-without-underpricing",
    title: "How to Win Work Without Underpricing It",
    excerpt: "You can be competitive and profitable. But it starts with knowing your numbers, not guessing them.",
    date: "2025-11-01",
    readTime: "4 min read",
    category: "Business",
    image: "https://customer-assets.emergentagent.com/job_builder-estimates/artifacts/o2qiosm3_Screenshot%202026-02-12%20151040.png",
  },
  {
    id: "simple-programme-every-builder-needs",
    title: "The Simple Programme Every Builder Needs (But Rarely Has)",
    excerpt: "You don't need MS Project. You need a clear sequence that shows what happens when, and what's holding everything up.",
    date: "2025-10-15",
    readTime: "5 min read",
    category: "Programme",
    image: "https://customer-assets.emergentagent.com/job_builder-estimates/artifacts/nsk0trd0_IMG_3006%20%281%29.jpeg",
  },
  {
    id: "estimating-renovations-extensions",
    title: "Why Renovations and Extensions Are Harder to Estimate",
    excerpt: "New builds are predictable. Renovations have surprises hiding behind every wall. Here's how to price for them.",
    date: "2025-10-01",
    readTime: "5 min read",
    category: "Residential",
    image: "https://customer-assets.emergentagent.com/job_builder-estimates/artifacts/wq9mb8f0_IMG_3050%20%281%29.jpeg",
  },
  {
    id: "boq-format-that-works",
    title: "The BOQ Format That Actually Works for Builders",
    excerpt: "Fancy QS formats look impressive. But if you can't use them for procurement or subbie quotes, what's the point?",
    date: "2025-09-15",
    readTime: "4 min read",
    category: "BOQ",
    image: "https://customer-assets.emergentagent.com/job_builder-estimates/artifacts/zawnz8f6_IMG_3087.JPG",
  },
  {
    id: "ground-conditions-contingency",
    title: "Ground Conditions: The Contingency Nobody Budgets For",
    excerpt: "Rock, water, clay, fill, ground conditions can turn a profitable job into a cashflow nightmare overnight.",
    date: "2025-09-01",
    readTime: "5 min read",
    category: "Civil",
    image: "https://customer-assets.emergentagent.com/job_builder-estimates/artifacts/2s8djtid_Screenshot%202026-02-12%20151009.png",
  },
  {
    id: "when-to-say-no-to-a-job",
    title: "When to Say No to a Job (Before It Costs You)",
    excerpt: "Not every job is worth winning. Here's how to spot the red flags before you've committed to a price.",
    date: "2025-08-15",
    readTime: "4 min read",
    category: "Business",
    image: "https://customer-assets.emergentagent.com/job_builder-estimates/artifacts/rw8whfg7_rickydee_69839_Extend_deck_to_edge._Hyper_Realistic._7207206f-fb46-414d-a0a4-ce5fd11eeaa7.png",
  },
];

const categories = ["All", "Estimating", "Residential", "Civil", "Labour", "Business", "BOQ", "Programme", "Services"];

export default function BlogPage() {
  usePageMeta("insights");
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState("All");

  const scrollToContact = () => {
    navigate("/#contact");
  };

  const filteredPosts = selectedCategory === "All" 
    ? blogPosts 
    : blogPosts.filter(post => post.category === selectedCategory);

  const featuredPost = blogPosts.find(post => post.featured);
  const regularPosts = filteredPosts.filter(post => !post.featured);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-NZ', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <Header onContactClick={scrollToContact} />
      
      <main className="pt-20 md:pt-24">
        {/* Hero Section */}
        <section className="py-16 md:py-24 bg-black text-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <span className="font-mono text-sm uppercase tracking-widest text-zinc-500">
              Blog
            </span>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl uppercase mt-4 mb-6">
              Builder's Notes
            </h1>
            <p className="font-body text-xl md:text-2xl text-zinc-300 max-w-3xl leading-relaxed">
              Practical estimating insights from the field. No theory, no fluff, just what works.
            </p>
          </div>
        </section>

        {/* Category Filter */}
        <section className="py-6 border-t-4 border-b-4 border-black bg-zinc-50">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 font-mono text-sm uppercase tracking-wider border-2 border-black transition-colors ${
                    selectedCategory === category
                      ? "bg-black text-white"
                      : "bg-white text-black hover:bg-zinc-100"
                  }`}
                  data-testid={`category-${category.toLowerCase()}`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Post */}
        {selectedCategory === "All" && featuredPost && (
          <section className="py-16 md:py-24 border-b-4 border-black">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
              <span className="font-mono text-sm uppercase tracking-widest text-zinc-500 mb-6 block">
                Featured
              </span>
              <Link
                to={`/blog/${featuredPost.id}`}
                className="grid md:grid-cols-2 gap-8 md:gap-12 group"
                data-testid="featured-post"
              >
                <div className="border-4 border-black overflow-hidden">
                  <img
                    src={featuredPost.image}
                    alt={featuredPost.title}
                    className="w-full h-64 md:h-80 object-cover grayscale group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="flex flex-col justify-center">
                  <div className="flex items-center gap-4 mb-4">
                    <span className="font-mono text-xs uppercase tracking-widest bg-black text-white px-3 py-1">
                      {featuredPost.category}
                    </span>
                    <span className="font-mono text-xs text-zinc-500 flex items-center gap-1">
                      <Calendar size={12} />
                      {formatDate(featuredPost.date)}
                    </span>
                  </div>
                  <h2 className="font-heading font-bold text-2xl sm:text-3xl md:text-4xl uppercase mb-4 group-hover:text-zinc-600 transition-colors">
                    {featuredPost.title}
                  </h2>
                  <p className="font-body text-zinc-600 mb-6 leading-relaxed">
                    {featuredPost.excerpt}
                  </p>
                  <div className="flex items-center gap-2 font-mono text-sm uppercase tracking-widest group-hover:gap-4 transition-all">
                    Read Article
                    <ArrowRight size={16} />
                  </div>
                </div>
              </Link>
            </div>
          </section>
        )}

        {/* Blog Grid */}
        <section className="py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {regularPosts.map((post) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.id}`}
                  className="group border-2 border-black bg-white hover:bg-zinc-50 transition-colors"
                  data-testid={`blog-card-${post.id}`}
                >
                  <div className="overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-48 object-cover grayscale group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-4 mb-3">
                      <span className="font-mono text-xs uppercase tracking-widest text-zinc-500">
                        {post.category}
                      </span>
                      <span className="font-mono text-xs text-zinc-400 flex items-center gap-1">
                        <Clock size={12} />
                        {post.readTime}
                      </span>
                    </div>
                    <h3 className="font-heading font-bold text-lg uppercase mb-3 group-hover:text-zinc-600 transition-colors">
                      {post.title}
                    </h3>
                    <p className="font-body text-sm text-zinc-600 line-clamp-2">
                      {post.excerpt}
                    </p>
                    <div className="mt-4 pt-4 border-t border-zinc-200 flex items-center justify-between">
                      <span className="font-mono text-xs text-zinc-400">
                        {formatDate(post.date)}
                      </span>
                      <ChevronRight size={16} className="text-zinc-400 group-hover:text-black transition-colors" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 md:py-24 border-t-4 border-black bg-black text-white">
          <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl uppercase mb-6">
              Ready to Price With Confidence?
            </h2>
            <p className="font-body text-zinc-400 mb-8 max-w-2xl mx-auto">
              Stop guessing. Get clean numbers, clear boundaries, and estimates you can stand behind.
            </p>
            <button
              onClick={scrollToContact}
              className="bg-white text-black h-14 px-8 uppercase font-bold tracking-widest border-2 border-white btn-brutal inline-flex items-center gap-3 hover:bg-zinc-200"
              data-testid="blog-cta"
            >
              Get A Quote
              <ArrowRight size={20} />
            </button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
